import { useCallback, useEffect, useRef, useState } from 'react';

interface StreamAnalyserReturn {
  amplitude: number;
  bands: [number, number, number];
  isActive: boolean;
  connectStream: (stream: MediaStream) => void;
  disconnect: () => void;
}

interface AnalyserConfig {
  fftSize: number;
  smoothingTimeConstant: number;
  visualSmoothing: number;
}

const DEFAULT_CONFIG: AnalyserConfig = {
  fftSize: 2048,
  smoothingTimeConstant: 0.3,
  visualSmoothing: 0.15,
};

export function useStreamAnalyser(
  config: Partial<AnalyserConfig> = {}
): StreamAnalyserReturn {
  const [amplitude, setAmplitude] = useState(0);
  const [bands, setBands] = useState<[number, number, number]>([0, 0, 0]);
  const [isActive, setIsActive] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const smoothedRMSRef = useRef(0);
  const smoothedBandsRef = useRef<[number, number, number]>([0, 0, 0]);

  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  const calculateRMS = useCallback((data: Uint8Array): number => {
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const value = data[i] ?? 0;
      const normalized = value / 255;
      sum += normalized * normalized;
    }
    return Math.sqrt(sum / data.length);
  }, []);

  const calculateBands = useCallback(
    (data: Uint8Array, sampleRate: number): [number, number, number] => {
      const nyquist = sampleRate / 2;
      const binWidth = nyquist / data.length;

      const getBandRMS = (minFreq: number, maxFreq: number): number => {
        const startBin = Math.floor(minFreq / binWidth);
        const endBin = Math.ceil(maxFreq / binWidth);
        let sum = 0;
        let count = 0;

        for (let i = startBin; i < Math.min(endBin, data.length); i++) {
          const value = data[i] ?? 0;
          const normalized = value / 255;
          sum += normalized * normalized;
          count++;
        }

        return count > 0 ? Math.sqrt(sum / count) : 0;
      };

      return [getBandRMS(60, 250), getBandRMS(250, 2000), getBandRMS(2000, 8000)];
    },
    []
  );

  const updateData = useCallback(() => {
    if (!analyserRef.current || !audioContextRef.current) {
      rafRef.current = requestAnimationFrame(updateData);
      return;
    }

    const frequencyData = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(frequencyData);

    const rawRMS = calculateRMS(frequencyData);
    const rawBands = calculateBands(frequencyData, audioContextRef.current.sampleRate);

    smoothedRMSRef.current +=
      (rawRMS - smoothedRMSRef.current) * mergedConfig.visualSmoothing;

    for (let i = 0; i < 3; i++) {
      const current = smoothedBandsRef.current[i] ?? 0;
      const raw = rawBands[i] ?? 0;
      smoothedBandsRef.current[i] =
        current + (raw - current) * mergedConfig.visualSmoothing;
    }

    setAmplitude(smoothedRMSRef.current);
    setBands([...smoothedBandsRef.current]);

    rafRef.current = requestAnimationFrame(updateData);
  }, [calculateRMS, calculateBands, mergedConfig.visualSmoothing]);

  const connectStream = useCallback(
    (stream: MediaStream) => {
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      if (!analyserRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = mergedConfig.fftSize;
        analyserRef.current.smoothingTimeConstant = mergedConfig.smoothingTimeConstant;
      }

      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(updateData);
      }

      setIsActive(true);
    },
    [mergedConfig.fftSize, mergedConfig.smoothingTimeConstant, updateData]
  );

  const disconnect = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }

    smoothedRMSRef.current = 0;
    smoothedBandsRef.current = [0, 0, 0];
    setAmplitude(0);
    setBands([0, 0, 0]);
    setIsActive(false);
  }, []);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    amplitude,
    bands,
    isActive,
    connectStream,
    disconnect,
  };
}
